import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Calendar, Clock, Video } from 'lucide-react'
import { formatDateTimeLocal, toIST, getISTDateString, getISTTimeString, istDateTimeLocalToUTC, getCurrentIST } from '../../utils/timezone'

interface LiveSessionFormData {
  title: string
  description: string
  instructor: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  duration: number
  meetingLink: string
  meetingPassword?: string
  category: string
  tags: string[]
  isActive: boolean
  price: number
  maxParticipants: number
  dailySchedule: DailyScheduleItem[]
  registrationDeadline?: string
}

interface DailyScheduleItem {
  date: string
  startTime: string
  endTime: string
  meetingLink: string
  meetingPassword?: string
  isActive: boolean
}

interface ValidationErrors {
  title?: string
  description?: string
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  registrationDeadline?: string
}

export default function LiveSessionForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<LiveSessionFormData>({
    title: '',
    description: '',
    instructor: 'Sudha Mam',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    duration: 0,
    meetingLink: '',
    meetingPassword: '',
    category: 'Workshop',
    tags: [],
    isActive: true,
    price: 0,
    maxParticipants: 0, // 0 means unlimited
    dailySchedule: [],
    registrationDeadline: ''
  })
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [showDailySchedule, setShowDailySchedule] = useState(false)

  const categories = ['Workshop', 'Q&A', 'Demo', 'Masterclass', 'Webinar']

  useEffect(() => {
    if (isEditMode && id) {
      fetchSession(id)
    }
  }, [id, isEditMode])

  useEffect(() => {
    const validate = () => {
      const newErrors: ValidationErrors = {}
      
      // Title validation
      if (formData.title && (formData.title.length < 5 || formData.title.length > 100)) {
        newErrors.title = 'Session title must be between 5 and 100 characters'
      }

      // Description validation
      if (formData.description && (formData.description.length < 10 || formData.description.length > 1000)) {
        newErrors.description = 'Session description must be between 10 and 1000 characters'
      }

      // Date and Time validation (compare in IST)
      if (formData.startDate && formData.startTime) {
        const sessionStartDateTime = new Date(`${formData.startDate}T${formData.startTime}`)
        const currentTimeIST = getCurrentIST()

        // Compare IST times
        if (sessionStartDateTime < currentTimeIST) {
          newErrors.startDate = 'Session cannot be scheduled in the past (IST)'
        }

        // Registration deadline validation
        if (formData.registrationDeadline) {
          const registrationDeadlineDateTime = new Date(formData.registrationDeadline)
          if (registrationDeadlineDateTime >= sessionStartDateTime) {
            newErrors.registrationDeadline = 'Registration deadline must be before session start time'
          }
        }
      }
      
      setErrors(newErrors)
    }
    validate()
  }, [formData])

  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`1970-01-01T${formData.startTime}`)
      let end = new Date(`1970-01-01T${formData.endTime}`)
      
      // If end time is earlier than or equal to start time, it means session crosses midnight
      // Add 1 day to end time for correct calculation
      if (end <= start) {
        end = new Date(end.getTime() + 24 * 60 * 60 * 1000)
      }
      
      const diff = (end.getTime() - start.getTime()) / (1000 * 60)
      setFormData(prev => ({ ...prev, duration: diff > 0 ? diff : 0 }))
    } else {
      setFormData(prev => ({ ...prev, duration: 0 }))
    }
  }, [formData.startTime, formData.endTime])

  // Generate daily schedule when dates change
  useEffect(() => {
    if (formData.startDate && formData.endDate && formData.startTime && formData.endTime) {
      generateDailySchedule()
    }
  }, [formData.startDate, formData.endDate, formData.startTime, formData.endTime])

  const generateDailySchedule = () => {
    const startDate = new Date(formData.startDate)
    const endDate = new Date(formData.endDate)
    const schedule: DailyScheduleItem[] = []

    for (let currentDate = new Date(startDate); currentDate <= endDate; currentDate.setDate(currentDate.getDate() + 1)) {
      schedule.push({
        date: currentDate.toISOString().split('T')[0],
        startTime: formData.startTime,
        endTime: formData.endTime,
        meetingLink: formData.meetingLink,
        meetingPassword: formData.meetingPassword || '',
        isActive: true
      })
    }

    setFormData(prev => ({ ...prev, dailySchedule: schedule }))
  }

  const fetchSession = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/live-sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        const { scheduledAt, startDate, endDate, dailySchedule, cost, maxParticipants, registrationDeadline, ...rest } = data.session

        // Convert dates to IST for display
        const sessionStartDateIST = startDate ? toIST(startDate) : toIST(scheduledAt)
        const sessionEndDateIST = endDate ? toIST(endDate) : toIST(scheduledAt)

        // Get time from scheduledAt or first daily schedule item
        let startTime = ''
        let endTime = ''

        if (dailySchedule && dailySchedule.length > 0) {
          startTime = dailySchedule[0].startTime
          endTime = dailySchedule[0].endTime
        } else if (scheduledAt) {
          const scheduleDateTime = toIST(scheduledAt)
          startTime = getISTTimeString(scheduleDateTime).substring(0, 5) // HH:MM format
          // Calculate end time based on duration
          const endDateTime = new Date(scheduleDateTime.getTime() + (rest.duration * 60000))
          endTime = getISTTimeString(endDateTime).substring(0, 5)
        }

        // Format registration deadline for datetime-local input (in IST)
        let registrationDeadlineFormatted = ''
        if (registrationDeadline) {
          registrationDeadlineFormatted = formatDateTimeLocal(registrationDeadline)
        }

        setFormData({
          ...rest,
          startDate: getISTDateString(sessionStartDateIST),
          endDate: getISTDateString(sessionEndDateIST),
          startTime,
          endTime,
          price: cost || 0,
          maxParticipants: maxParticipants || 0, // 0 means unlimited
          dailySchedule: dailySchedule || [],
          registrationDeadline: registrationDeadlineFormatted
        })
      }
    } catch (error) {
      console.error('Error fetching session:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    
    // Handle maxParticipants specially to convert empty string to 0 for unlimited
    if (name === 'maxParticipants') {
      const numValue = value === '' ? 0 : parseInt(value) || 0
      setFormData(prev => ({ ...prev, [name]: numValue }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (Object.keys(errors).length > 0) {
      alert('Please fix the validation errors before submitting.')
      return
    }
    setLoading(true)

    // Exclude fields that shouldn't be sent or will be computed
    const { dailySchedule, registrationDeadline, startDate, startTime, endDate, endTime, price, ...restData } = formData

    // Convert IST date/time to UTC for storage using helper function
    const istDateTime = `${startDate}T${startTime}`
    const scheduledAt = istDateTimeLocalToUTC(istDateTime)

    // Convert registration deadline from IST to UTC if present
    const deadlineUTC = registrationDeadline ? istDateTimeLocalToUTC(registrationDeadline) : null

    const payload = {
      ...restData,
      startDate,
      endDate,
      startTime,
      endTime,
      scheduledAt,
      cost: price,
      maxParticipants: formData.maxParticipants || 0,
      registrationDeadline: deadlineUTC
    }

    try {
      const token = localStorage.getItem('token')
      const url = isEditMode ? `/api/admin/live-sessions/${id}` : '/api/admin/live-sessions'
      const method = isEditMode ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        alert(`Session ${isEditMode ? 'updated' : 'created'} successfully!`)
        navigate('/admin/live-sessions')
      } else {
        const errorData = await response.json()
        alert(errorData.message || 'Failed to save session')
      }
    } catch (error) {
      console.error('Error saving session:', error)
      alert('An error occurred while saving the session.')
    } finally {
      setLoading(false)
    }
  }
  
    const addTag = () => {
    const tag = prompt('Enter tag:')
    if (tag) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }))
    }
  }

  const removeTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }))
  }

  const updateDailyScheduleItem = (index: number, field: keyof DailyScheduleItem, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      dailySchedule: prev.dailySchedule.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }))
  }

  const getTotalDays = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate)
      const end = new Date(formData.endDate)
      const diffTime = Math.abs(end.getTime() - start.getTime())
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
    }
    return 1
  }


  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/live-sessions')}
          className="p-2 hover:bg-charcoal-100 dark:hover:bg-charcoal-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-charcoal-900 dark:text-white">
            {isEditMode ? 'Edit Live Session' : 'Schedule New Session'}
          </h1>
          <p className="text-charcoal-600 dark:text-charcoal-400">
            Create and manage live online classes
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-6 border border-charcoal-200 dark:border-charcoal-800">
          <h2 className="text-xl font-semibold text-charcoal-900 dark:text-white mb-4">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Session Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="e.g., Advanced Bridal Makeup Live Workshop"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="Describe what will be covered in this session..."
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Instructor *
              </label>
              <input
                type="text"
                name="instructor"
                value={formData.instructor}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="Enter instructor's name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-6 border border-charcoal-200 dark:border-charcoal-800">
          <h2 className="text-xl font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Schedule & Duration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
              />
              {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                End Date *
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                required
                min={formData.startDate} // End date cannot be before start date
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                End Time *
              </label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Session Summary
              </label>
              <div className="bg-charcoal-50 dark:bg-charcoal-800 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Total Days:</span>
                    <span className="ml-2">{getTotalDays()} day{getTotalDays() > 1 ? 's' : ''}</span>
                  </div>
                  <div>
                    <span className="font-medium">Daily Duration:</span>
                    <span className="ml-2">{formData.duration > 0 ? `${formData.duration} minutes` : 'Not set'}</span>
                  </div>
                  <div>
                    <span className="font-medium">Total Hours:</span>
                    <span className="ml-2">{formData.duration > 0 ? `${((formData.duration * getTotalDays()) / 60).toFixed(1)} hours` : 'Not set'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Schedule Management */}
        {formData.dailySchedule.length > 0 && (
          <div className="bg-white dark:bg-charcoal-900 rounded-xl p-6 border border-charcoal-200 dark:border-charcoal-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-charcoal-900 dark:text-white flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Daily Schedule Management
              </h2>
              <button
                type="button"
                onClick={() => setShowDailySchedule(!showDailySchedule)}
                className="text-rose-600 hover:text-rose-700 font-medium"
              >
                {showDailySchedule ? 'Hide' : 'Show'} Details
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Note:</strong> You can customize meeting links and times for each day. This is useful if you need different Zoom rooms or timing adjustments for specific days.
              </p>
            </div>

            {showDailySchedule && (
              <div className="space-y-4">
                {formData.dailySchedule.map((day, index) => (
                  <div key={index} className="border border-charcoal-200 dark:border-charcoal-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="font-medium text-charcoal-900 dark:text-white">
                        Day {index + 1}: {new Date(day.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </h3>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={day.isActive}
                          onChange={(e) => updateDailyScheduleItem(index, 'isActive', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-rose-300 dark:peer-focus:ring-rose-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-rose-600"></div>
                        <span className="ml-2 text-sm text-charcoal-600 dark:text-charcoal-400">Active</span>
                      </label>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-1">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={day.startTime}
                          onChange={(e) => updateDailyScheduleItem(index, 'startTime', e.target.value)}
                          className="w-full px-3 py-2 border border-charcoal-300 dark:border-charcoal-700 rounded-lg bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-1">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={day.endTime}
                          onChange={(e) => updateDailyScheduleItem(index, 'endTime', e.target.value)}
                          className="w-full px-3 py-2 border border-charcoal-300 dark:border-charcoal-700 rounded-lg bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-1">
                          Meeting Link
                        </label>
                        <input
                          type="url"
                          value={day.meetingLink}
                          onChange={(e) => updateDailyScheduleItem(index, 'meetingLink', e.target.value)}
                          className="w-full px-3 py-2 border border-charcoal-300 dark:border-charcoal-700 rounded-lg bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                          placeholder="https://zoom.us/j/..."
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-1">
                          Meeting Password (Optional)
                        </label>
                        <input
                          type="text"
                          value={day.meetingPassword || ''}
                          onChange={(e) => updateDailyScheduleItem(index, 'meetingPassword', e.target.value)}
                          className="w-full px-3 py-2 border border-charcoal-300 dark:border-charcoal-700 rounded-lg bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                          placeholder="Optional password"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-6 border border-charcoal-200 dark:border-charcoal-800">
          <h2 className="text-xl font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
            <Video className="h-5 w-5" />
            Meeting Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Meeting Link (Zoom/Google Meet/Jitsi) *
              </label>
              <input
                type="url"
                name="meetingLink"
                value={formData.meetingLink}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="https://zoom.us/j/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Meeting Password (Optional)
              </label>
              <input
                type="text"
                name="meetingPassword"
                value={formData.meetingPassword || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="Optional password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Price (₹) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Max Participants
              </label>
              <input
                type="number"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                placeholder="Leave empty for unlimited (or enter a number)"
              />
              <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-1">
                Leave empty or set to 0 for unlimited registrations
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
                Registration Deadline (Optional)
              </label>
              <input
                type="datetime-local"
                name="registrationDeadline"
                value={formData.registrationDeadline || ''}
                onChange={handleInputChange}
                max={formData.startDate && formData.startTime ? `${formData.startDate}T${formData.startTime}` : undefined}
                className="w-full px-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500"
              />
              {errors.registrationDeadline && <p className="text-red-500 text-sm mt-1">{errors.registrationDeadline}</p>}
              <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-1">
                Last date for students to register for this session (must be before session start time)
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-900 rounded-xl p-6 border border-charcoal-200 dark:border-charcoal-800">
          <h2 className="text-xl font-semibold text-charcoal-900 dark:text-white mb-4">
            Tags
          </h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {formData.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-rose-100 dark:bg-mauve-900/20 text-mauve-800 dark:text-rose-300 rounded-full text-sm flex items-center gap-2"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={addTag}
            className="text-sm text-rose-600 hover:text-rose-700"
          >
            + Add Tag
          </button>
        </div>

        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/live-sessions')}
            className="px-6 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl hover:bg-charcoal-50 dark:hover:bg-charcoal-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || Object.keys(errors).length > 0}
            className="flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {loading ? 'Saving...' : isEditMode ? 'Update Session' : 'Schedule Session'}
          </button>
        </div>
      </form>
    </div>
  )
}